import React from "react";
import * as PropTypes from "prop-types";

const numberFormat = new Intl.NumberFormat("pt-BR");

function UIKpiCards({ items }) {
	if( !items || !items.length ) return null;

	return (
		<div className={`kpi-cards ${items.length < 3 ? "kpi-cards-compact" : ""}`}>
			{items.map((item) => {
				const clickable = typeof item.onClick === "function";
				const Tag = clickable ? "button" : "div";
				const value = typeof item.value === "number" ? numberFormat.format(item.value) : (item.value ?? "—");

				return (
					<Tag
						key={item.key}
						{...(clickable ? { type: "button", onClick: item.onClick, "aria-pressed": !!item.active } : {})}
						className={`kpi-card kpi-card-${item.tone || "blue"} ${clickable ? "kpi-card-clickable" : ""} ${item.active ? "kpi-card-active" : ""}`}
					>
						<span className="kpi-card-icon">{item.icon}</span>
						<span className="kpi-card-info">
							<span className="kpi-card-title">{item.title}</span>
							<strong className="kpi-card-value">{value}</strong>
							{item.caption && <span className="kpi-card-caption">{item.caption}</span>}
						</span>
					</Tag>
				);
			})}
		</div>
	);
}

UIKpiCards.propTypes = {
	items: PropTypes.arrayOf(
		PropTypes.shape({
			key    : PropTypes.string.isRequired,
			icon   : PropTypes.node,
			tone   : PropTypes.oneOf(["blue", "green", "violet", "amber", "red", "neutral"]),
			title  : PropTypes.string.isRequired,
			value  : PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.node]),
			caption: PropTypes.node,
			onClick: PropTypes.func,
			active : PropTypes.bool,
		}),
	),
};

/**
 * Helper para montar cards padrão de status (Total / Ativos / Inativos) a partir dos kpis da API.
 *
 * @param {Object}   options
 * @param {Object}   options.kpis       { total, ativos, inativos } vindo da API
 * @param {Node}     options.icon       ícone do card de total
 * @param {string}   options.title      título do card de total (ex.: "Total de usuários")
 * @param {string}   options.caption    legenda do card de total (ex.: "Usuários cadastrados")
 * @param {*}        options.status     valor atual do filtro de status (1, 0 ou null)
 * @param {Function} options.onStatusChange recebe 1, 0 ou null (clicar no card já ativo limpa o filtro)
 */
export function statusKpis({ kpis, icon, title, caption, status, onStatusChange }) {
	const total = kpis?.total ?? null;
	const percent = (count) => (total ? `${Math.round((count / total) * 100)}% do total` : "0% do total");
	const toggle = (value) => () => onStatusChange(status === value ? null : value);

	return [
		{
			key    : "total",
			tone   : "blue",
			icon,
			title,
			value  : total ?? "—",
			caption,
		},
		{
			key    : "active",
			tone   : "green",
			icon   : <i className="fal fa-check-circle" />,
			title  : "Ativos",
			value  : kpis?.ativos ?? "—",
			caption: percent(kpis?.ativos ?? 0),
			onClick: toggle(1),
			active : status === 1,
		},
		{
			key    : "inactive",
			tone   : "violet",
			icon   : <i className="fal fa-pause-circle" />,
			title  : "Inativos",
			value  : kpis?.inativos ?? "—",
			caption: percent(kpis?.inativos ?? 0),
			onClick: toggle(0),
			active : status === 0,
		},
	];
}

export function kpisAfterToggle(kpis, wasActive, isActive) {
	if( !kpis || !!wasActive === !!isActive ) return kpis;

	const delta = isActive ? 1 : -1;

	return {
		...kpis,
		ativos  : (kpis.ativos ?? 0) + delta,
		inativos: (kpis.inativos ?? 0) - delta,
	};
}

export default UIKpiCards;
